import React, { useState, useEffect } from "react";
import Card from "../../components/Card";
import Action from "../../components/Action";
import TextQuestion from "../../components/TextQuestion";
import ScrollToTop from "../../components/utils/ScrollToTop";
import Image from "../../components/Image";
import imageCatAndHousePlant from "../../images/cat-and-houseplant.png";
import RadioQuestion from "../../components/RadioQuestion";
import { YES_NO_OPTIONS } from "../../constants";
import { isMoneyValid, normaliseMoney } from "../../shared/utils/moneyUtils";
import { getOrdinalString } from "../../shared/utils/numberUtils";
import { isEmailValid } from "../../shared/utils/validation";
import { isPhoneNumberValid } from "../../shared/utils/numberUtils";

const defaultErrorMessages = {
  lendingMoneyFullName: "Please fill this in",
  lendingMoneyAmount: "Please fill this in",
  lendingMoneySecured: "Please choose an option",
  lendingMoneyEmailAddress: "Please enter an email address",
  lendingMoneyPhoneNumber: "Please fill this in",
  lendingMoneyAddressAndPostcode: "Please fill this in",
  lendingMoneyRelationship: "Please fill this in",
};

const initStateFromProps = (lendingMoney) => {
  if (lendingMoney) {
    return {
      lendingMoneyFullName: lendingMoney.lendingMoneyFullName || "",
      lendingMoneyAmount: lendingMoney.lendingMoneyAmount || "",
      lendingMoneySecured: lendingMoney.lendingMoneySecured ?? null,
      lendingMoneyEmailAddress: lendingMoney.lendingMoneyEmailAddress || "",
      lendingMoneyPhoneNumber: lendingMoney.lendingMoneyPhoneNumber || "",
      lendingMoneyAddressAndPostcode: lendingMoney.lendingMoneyAddressAndPostcode || "",
      lendingMoneyRelationship: lendingMoney.lendingMoneyRelationship || "",
    };
  }
  return {
    lendingMoneyFullName: "",
    lendingMoneyAmount: "",
    lendingMoneySecured: null,
    lendingMoneyEmailAddress: "",
    lendingMoneyPhoneNumber: "",
    lendingMoneyAddressAndPostcode: "",
    lendingMoneyRelationship: "",
  };
};

const LendingMoneyForm = ({ lendingMoney, lenderNumber, onStepComplete }) => {
  const [formState, setFormState] = useState(initStateFromProps(lendingMoney));
  const [errors, setErrors] = useState({});
  const [errorMessages, setErrorMessages] = useState({ ...defaultErrorMessages });

  // Reset form state when props change (e.g., navigating between lenders)
  useEffect(() => {
    setFormState(initStateFromProps(lendingMoney));
    setErrors({});
    setErrorMessages({ ...defaultErrorMessages });
  }, [lendingMoney, lenderNumber]);

  const handleInputChange = (field) => (value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleValidation = () => {
    let newErrors = {};
    let newErrorMessages = { ...errorMessages };
    let formIsValid = true;

    if (!formState.lendingMoneyFullName) {
      newErrors.lendingMoneyFullName = true;
      formIsValid = false;
    }

    if (!formState.lendingMoneyAmount) {
      newErrors.lendingMoneyAmount = true;
      formIsValid = false;
    }

    if (formState.lendingMoneyAmount !== "" && !isMoneyValid(formState.lendingMoneyAmount)) {
      newErrors.lendingMoneyAmount = true;
      newErrorMessages.lendingMoneyAmount = "Please enter a valid amount";
      formIsValid = false;
    }

    if (formState.lendingMoneySecured === null) {
      newErrors.lendingMoneySecured = true;
      formIsValid = false;
    }

    if (!formState.lendingMoneyEmailAddress) {
      newErrors.lendingMoneyEmailAddress = true;
      newErrorMessages.lendingMoneyEmailAddress = "Please enter an email address";
      formIsValid = false;
    }

    if (formState.lendingMoneyEmailAddress !== "" && !isEmailValid(formState.lendingMoneyEmailAddress)) {
      newErrors.lendingMoneyEmailAddress = true;
      newErrorMessages.lendingMoneyEmailAddress = "Please enter a valid email address";
      formIsValid = false;
    }

    if (!formState.lendingMoneyPhoneNumber) {
      newErrors.lendingMoneyPhoneNumber = true;
      formIsValid = false;
    }

    if (formState.lendingMoneyPhoneNumber !== "" && !isPhoneNumberValid(formState.lendingMoneyPhoneNumber)) {
      newErrors.lendingMoneyPhoneNumber = true;
      newErrorMessages.lendingMoneyPhoneNumber = "Please enter a valid phone number";
      formIsValid = false;
    }

    if (!formState.lendingMoneyAddressAndPostcode) {
      newErrors.lendingMoneyAddressAndPostcode = true;
      formIsValid = false;
    }

    if (!formState.lendingMoneyRelationship) {
      newErrors.lendingMoneyRelationship = true;
      formIsValid = false;
    }

    setErrors(newErrors);
    setErrorMessages(newErrorMessages);
    return formIsValid;
  };

  const handleFieldValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (!formState[field]) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
  };

  const handleRadioValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (formState[field] === null) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
  };

  const handlePhoneNumberValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (!formState[field]) {
          newErrors[field] = true;
        } else if (!isPhoneNumberValid(formState[field])) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
    setErrorMessages((prev) => {
      let newErrorMessages = { ...prev };

      if (isBlur) {
        if (!formState[field]) {
          newErrorMessages[field] = "Please fill this in";
        } else if (!isPhoneNumberValid(formState[field])) {
          newErrorMessages[field] = "Please enter a valid phone number";
        }
      }

      return newErrorMessages;
    });
  };

  const handleEmailAddressValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (!formState[field]) {
          newErrors[field] = true;
        } else if (!isEmailValid(formState[field])) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
    setErrorMessages((prev) => {
      let newErrorMessages = { ...prev };

      if (isBlur) {
        if (!formState[field]) {
          newErrorMessages[field] = "Please enter an email address";
        } else if (!isEmailValid(formState[field])) {
          newErrorMessages[field] = "Please enter a valid email address";
        }
      }

      return newErrorMessages;
    });
  };

  const handleMoneyValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    setErrors((prev) => {
      let newErrors = { ...prev };

      if (!isBlur) {
        newErrors[field] = false;
      } else {
        if (!formState[field]) {
          newErrors[field] = true;
        } else if (!isMoneyValid(formState[field])) {
          newErrors[field] = true;
        }
      }

      return newErrors;
    });
    setErrorMessages((prev) => {
      let newErrorMessages = { ...prev };

      if (isBlur) {
        if (!formState[field]) {
          newErrorMessages[field] = "Please fill this in";
        } else if (!isMoneyValid(formState[field])) {
          newErrorMessages[field] = "Please enter a valid amount";
        }
      }

      return newErrorMessages;
    });
  };

  const saveLendingMoney = (event) => {
    event.preventDefault();

    if (handleValidation()) {
      const outLending = {};
      outLending["lendingMoney" + lenderNumber] = {
        lendingMoneyFullName: formState.lendingMoneyFullName,
        lendingMoneyAmount: normaliseMoney(formState.lendingMoneyAmount),
        lendingMoneySecured: formState.lendingMoneySecured,
        lendingMoneyEmailAddress: formState.lendingMoneyEmailAddress,
        lendingMoneyPhoneNumber: formState.lendingMoneyPhoneNumber,
        lendingMoneyAddressAndPostcode: formState.lendingMoneyAddressAndPostcode,
        lendingMoneyRelationship: formState.lendingMoneyRelationship,
      };

      onStepComplete({
        ...outLending,
      });
    }
  };

  const ordinalString = getOrdinalString(lenderNumber);

  return (
    <React.Fragment>
      <ScrollToTop />
      <Card title={"Tell us about the " + ordinalString + " person lending you money"} removeTopPadding>
        <p>
          We'll need to contact and verify the identity of anyone lending you money - to complete our anti-money
          laundering checks.
        </p>
        <TextQuestion
          question={"What is the name of the " + ordinalString + " person lending you money?"}
          value={formState.lendingMoneyFullName}
          valueEntered={handleInputChange("lendingMoneyFullName")}
          validate={handleFieldValidation("lendingMoneyFullName")}
          validationError={errors.lendingMoneyFullName ? errorMessages.lendingMoneyFullName : null}
        />

        <TextQuestion
          question="How much will they be lending you?"
          value={formState.lendingMoneyAmount}
          valueEntered={handleInputChange("lendingMoneyAmount")}
          validate={handleMoneyValidation("lendingMoneyAmount")}
          validationError={errors.lendingMoneyAmount ? errorMessages.lendingMoneyAmount : null}
        />

        <RadioQuestion
          question="Will this loan be secured against your property?"
          options={YES_NO_OPTIONS}
          inline={true}
          name="lendingMoneySecured"
          selected={formState.lendingMoneySecured}
          optionSelected={handleInputChange("lendingMoneySecured")}
          validate={handleRadioValidation("lendingMoneySecured")}
          validationError={errors.lendingMoneySecured ? errorMessages.lendingMoneySecured : null}
        />

        <TextQuestion
          question="What’s their email address?"
          value={formState.lendingMoneyEmailAddress}
          valueEntered={handleInputChange("lendingMoneyEmailAddress")}
          validate={handleEmailAddressValidation("lendingMoneyEmailAddress")}
          validationError={errors.lendingMoneyEmailAddress ? errorMessages.lendingMoneyEmailAddress : null}
        />

        <TextQuestion
          question="What’s their phone number?"
          value={formState.lendingMoneyPhoneNumber}
          valueEntered={handleInputChange("lendingMoneyPhoneNumber")}
          validate={handlePhoneNumberValidation("lendingMoneyPhoneNumber")}
          validationError={errors.lendingMoneyPhoneNumber ? errorMessages.lendingMoneyPhoneNumber : null}
        />

        <TextQuestion
          question="What’s their address?"
          value={formState.lendingMoneyAddressAndPostcode}
          valueEntered={handleInputChange("lendingMoneyAddressAndPostcode")}
          validate={handleFieldValidation("lendingMoneyAddressAndPostcode")}
          validationError={errors.lendingMoneyAddressAndPostcode ? errorMessages.lendingMoneyAddressAndPostcode : null}
        />

        <TextQuestion
          question="What’s their relationship to you?"
          value={formState.lendingMoneyRelationship}
          valueEntered={handleInputChange("lendingMoneyRelationship")}
          validate={handleFieldValidation("lendingMoneyRelationship")}
          validationError={errors.lendingMoneyRelationship ? errorMessages.lendingMoneyRelationship : null}
        />

        <Action onClick={saveLendingMoney}>Continue</Action>
      </Card>

      <Image src={imageCatAndHousePlant} />
    </React.Fragment>
  );
};

export default LendingMoneyForm;
